import React, { useState } from 'react';
import {
  Button,
  Table,
  Avatar,
  Modal,
  InputNumber,
  Input,
  QRCode,
  Empty,
  message,
} from 'antd';
import {
  ArrowLeft,
  QrCode,
  HandCoins,
  Clock,
  Info,
  PencilLine,
  Wallet,
  Plus,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import {
  DonateCampaign,
  campaignState,
  isGoalReached,
} from './useDonateCampaign';
import { DottedProgress } from './dotted-progress';
import { PostUpdateModal } from './post-update-modal';
import { WithdrawModal } from './withdraw-modal';
import { EditContentModal } from './edit-content-modal';
import { EkycCameraModal } from './ekyc-camera-modal';
import { getEkycStatus } from '../../api/user/ekyc.api';
import { updateBankAccount } from '../../api/payment/user-bank.api';

dayjs.extend(relativeTime);
dayjs.locale('vi');

// màu lấy đúng từ design Figma
const BRAND = '#066BFF'; // button chính
const CYAN = '#0072C3'; // "% to complete"
const CARD_SHADOW = '4px 4px 16px rgba(0,0,0,0.08)';

// các mức ủng hộ nhanh (Ca)
const DONATE_PRESETS = [1000, 5000, 10000, 50000, 100000];

const fmt = (n: number) => `${n}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const tagOf = (c: DonateCampaign) => {
  const s = campaignState(c);
  if (s === 'disbursed')
    return { text: 'Đã giải ngân', cls: 'text-[#16A34A] bg-[#F0FDF4] border-[#86EFAC]' };
  if (s === 'disbursing')
    return { text: 'Đang giải ngân', cls: 'text-[#066BFF] bg-[#EDF5FF] border-[#93C5FD]' };
  if (s === 'stopped')
    return isGoalReached(c)
      ? { text: 'Đã đạt mục tiêu', cls: 'text-[#D97706] bg-[#FFFBEB] border-[#FCD34D]' }
      : { text: 'Đã kết thúc', cls: 'text-slate-500 bg-slate-100 border-slate-300' };
  return { text: 'Đang hoạt động', cls: 'text-[#22C55E] bg-[#F0FDF4] border-[#86EFAC]' };
};

const StatusTag: React.FC<{ text: string; cls: string }> = ({ text, cls }) => (
  <span className={`rounded-full border px-3 py-0.5 text-[13px] font-medium ${cls}`}>
    {text}
  </span>
);

interface Props {
  campaign: DonateCampaign;
  isOwner?: boolean;
  onBack: () => void;
  onDonate: (amount: number, message?: string) => Promise<void> | void;
  onWithdraw: () => Promise<void> | void;
  onPostUpdate: (content: string, imageUrl?: string) => Promise<void> | void;
  onEdit: (patch: { title: string; content: string; imageUrl?: string }) => Promise<void> | void;
}

export const CampaignDetailPage: React.FC<Props> = ({
  campaign: c,
  isOwner = false,
  onBack,
  onDonate,
  onWithdraw,
  onPostUpdate,
  onEdit,
}) => {
  const [qrOpen, setQrOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [msg, setMsg] = useState('');

  // Bấm "Yêu cầu rút tiền": chưa eKYC → mở luồng camera eKYC; đã eKYC → modal rút.
  const handleWithdrawClick = async () => {
    try {
      const st = await getEkycStatus();
      if (st.isVerified) setWithdrawOpen(true);
      else setEkycOpen(true);
    } catch {
      setEkycOpen(true);
    }
  };

  const state = campaignState(c);
  const tag = tagOf(c);
  const pct =
    c.targetAmount > 0
      ? Math.min(100, Math.round((c.raised / c.targetAmount) * 100))
      : 0;
  const daysLeft =
    c.goalType === 'amount_time' && c.endAt
      ? dayjs(c.endAt).diff(dayjs(), 'day')
      : null;
  const profileUrl = `${window.location.origin}/user/${c.ownerId}`;

  const submitDonate = async () => {
    if (!amount || amount <= 0) {
      message.error('Nhập số Ca muốn ủng hộ');
      return;
    }
    try {
      await onDonate(amount, msg);
      setAmount(null);
      setMsg('');
      setDonateOpen(false);
      message.success(`Đã ủng hộ ${fmt(amount)} ca`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Ủng hộ thất bại');
    }
  };

  // ----- Nút top-right theo trạng thái -----
  const renderActions = () => {
    const qrBtn = (
      <Button shape="round" icon={<QrCode size={16} />} onClick={() => setQrOpen(true)}>
        Xem QR
      </Button>
    );

    if (state === 'active') {
      return (
        <>
          {qrBtn}
          {isOwner && (
            <Button
              shape="round"
              icon={<PencilLine size={16} />}
              onClick={() => setEditOpen(true)}
            >
              Chỉnh sửa nội dung
            </Button>
          )}
          {/* Ủng hộ: hiện cho mọi người, kể cả chủ chiến dịch (chủ có thể tự ủng hộ) */}
          <Button
            type="primary"
            shape="round"
            icon={<HandCoins size={16} />}
            style={{ background: BRAND, borderColor: BRAND }}
            onClick={() => setDonateOpen(true)}
          >
            Ủng hộ ngay
          </Button>
        </>
      );
    }

    // ngừng hoạt động / đang giải ngân / đã giải ngân
    if (isOwner) {
      return (
        <Button
          type="primary"
          shape="round"
          icon={<Wallet size={16} />}
          disabled={state !== 'stopped'}
          style={
            state === 'stopped'
              ? { background: BRAND, borderColor: BRAND }
              : undefined
          }
          onClick={handleWithdrawClick}
        >
          Yêu cầu quyết toán thù lao
        </Button>
      );
    }
    return qrBtn;
  };

  return (
    <div className="flex flex-col gap-12 py-2">
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#066BFF] mb-4"
        >
          <ArrowLeft size={16} /> Quay lại
        </button>

        {/* Banner */}
        {c.imageUrl ? (
          <img
            src={c.imageUrl}
            alt={c.title}
            className="w-full h-[300px] object-cover rounded-[20px]"
          />
        ) : (
          <div className="w-full h-[300px] rounded-[20px] bg-gradient-to-r from-sky-500 to-indigo-600" />
        )}

        {/* Title row */}
        <div className="flex flex-wrap items-start justify-between gap-3 mt-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-[28px] font-semibold leading-tight tracking-tight">
                {c.title}
              </h2>
              <StatusTag text={tag.text} cls={tag.cls} />
            </div>
            <div className="text-[18px] text-slate-500 mt-1">
              by <span className="text-[#066BFF] font-medium">@{c.ownerName}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">{renderActions()}</div>
        </div>

        {/* Khối giữa: đã giải ngân → tóm tắt giao dịch; còn lại → thanh tiến độ */}
        {state === 'disbursed' ? (
          <div
            className="rounded-[12px] bg-white dark:bg-dark2 p-6 mt-4 flex flex-col"
            style={{ boxShadow: CARD_SHADOW }}
          >
            {[
              ['Số Ca đã rút', `${fmt(c.netAmount ?? 0)} ca`],
              ['Mã giao dịch', c.txId || '—'],
              [
                'Thời gian',
                c.disbursedAt ? dayjs(c.disbursedAt).format('DD/MM/YYYY HH:mm') : '—',
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
              >
                <span className="text-[15px] text-slate-500">{label}</span>
                <span className="text-[16px] font-semibold text-[#212b4e] dark:text-slate-100">
                  {value}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="rounded-[12px] bg-white dark:bg-dark2 p-6 mt-4"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 text-[13px] text-slate-500">
                  <span className="font-medium" style={{ color: BRAND }}>
                    {tag.text}
                  </span>
                  {daysLeft !== null ? (
                    <span className="flex items-center gap-1">
                      <Clock size={13} />{' '}
                      {daysLeft > 0 ? `Còn ${daysLeft} ngày` : 'Đã hết hạn'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Info size={13} /> Không giới hạn thời gian
                    </span>
                  )}
                </div>
                <div className="text-[28px] font-semibold text-black dark:text-white leading-none tracking-tight">
                  {fmt(c.raised)}{' '}
                  <span className="text-[16px] font-normal text-slate-400">
                    / {fmt(c.targetAmount)} ca
                  </span>
                </div>
              </div>
              <div className="w-[328px] max-w-full">
                <div className="text-right font-bold mb-1" style={{ color: CYAN }}>
                  Đã đạt {pct}%
                </div>
                <DottedProgress pct={pct} />
              </div>
            </div>

            {/* Đang giải ngân: chờ admin xác nhận chi trả lệnh rút (luồng eKYC) */}
            {isOwner && state === 'disbursing' && (
              <p className="mt-4 text-[14px] text-[#066BFF]">
                Yêu cầu quyết toán đang được xử lý — sẽ chuyển sang trạng thái Đã giải
                ngân khi quản trị xác nhận chi trả.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Minh bạch: chiến dịch kết thúc → chủ đã rút tiền chưa (mọi người đều thấy) */}
      {state !== 'active' && (
        <div
          className={`rounded-[12px] p-4 text-[14px] border ${
            state === 'disbursed'
              ? 'bg-[#F0FDF4] border-[#86EFAC] text-[#16A34A]'
              : state === 'disbursing'
              ? 'bg-[#EDF5FF] border-[#93C5FD] text-[#066BFF]'
              : 'bg-[#FFFBEB] border-[#FCD34D] text-[#B45309]'
          }`}
        >
          {state === 'disbursed' ? (
            <>
              <b>Đã giải ngân</b> — chủ chiến dịch đã rút {fmt(c.netAmount ?? c.raised)} ca
              {c.txId ? ` · Mã GD ${c.txId}` : ''}
              {c.disbursedAt ? ` · ${dayjs(c.disbursedAt).format('DD/MM/YYYY HH:mm')}` : ''}.
            </>
          ) : state === 'disbursing' ? (
            <>
              <b>Đang giải ngân</b> — đã yêu cầu quyết toán, chờ quản trị xác nhận chi trả cho chủ chiến dịch.
            </>
          ) : (
            <>
              <b>Chiến dịch đã kết thúc</b> — chủ chiến dịch chưa quyết toán thù lao.
            </>
          )}
        </div>
      )}

      {/* Cập nhật từ chiến dịch — chỉ hiện khi chiến dịch đang hoạt động */}
      {state === 'active' && (
        <div>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-[24px] font-semibold tracking-tight">
                Cập nhật từ chiến dịch
              </h3>
              <p className="text-[16px] text-slate-500 mt-1">
                Những thông tin mới nhất về tiến độ và hoạt động của chiến dịch
              </p>
            </div>
            {isOwner && (
              <Button
                type="primary"
                shape="round"
                icon={<Plus size={16} />}
                style={{ background: BRAND, borderColor: BRAND }}
                onClick={() => setPostOpen(true)}
              >
                Đăng bài
              </Button>
            )}
          </div>

          {c.updates.length === 0 ? (
            <Empty
              description="Chưa có bài cập nhật nào"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {c.updates.map((u) => (
                <div
                  key={u.id}
                  className="rounded-[12px] bg-white dark:bg-dark2 p-6 flex flex-col gap-3"
                  style={{ boxShadow: CARD_SHADOW }}
                >
                  <div className="flex items-center gap-3">
                    <Avatar size={36}>
                      {(u.authorName || 'U')[0]?.toUpperCase()}
                    </Avatar>
                    <div className="flex flex-col gap-1">
                      <div className="font-medium text-[16px] text-[#262626] dark:text-slate-100">
                        {u.authorName}
                      </div>
                      <div className="flex items-center gap-1 text-[14px] text-[#6f6f6b]">
                        <Clock size={14} /> Đăng {dayjs(u.at).fromNow()}
                      </div>
                    </div>
                  </div>
                  {u.imageUrl && (
                    <img
                      src={u.imageUrl}
                      alt=""
                      className="w-full max-h-[220px] object-cover rounded-[8px]"
                    />
                  )}
                  <p className="whitespace-pre-wrap text-[16px] text-[#232322] dark:text-slate-200 leading-6">
                    {u.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Danh sách người đã ủng hộ */}
      <div>
        <h3 className="text-[24px] font-semibold tracking-tight mb-4">
          Danh sách người đã ủng hộ
        </h3>
        {c.donors.length === 0 ? (
          <Empty description="Chưa có ai ủng hộ" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div
            className="rounded-[24px] bg-white dark:bg-dark2 overflow-hidden"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <Table
              size="middle"
              rowKey={(_, i) => String(i)}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              dataSource={c.donors}
              columns={[
                { title: 'STT', align: 'center', width: 70, render: (_v, _r, i) => i + 1 },
                {
                  title: 'Username',
                  dataIndex: 'userName',
                  render: (v: string) => (
                    <span className="flex items-center gap-3">
                      <Avatar size={28}>{(v || 'U')[0]?.toUpperCase()}</Avatar>
                      {v}
                    </span>
                  ),
                },
                {
                  title: 'Số Ca đã ủng hộ',
                  dataIndex: 'amount',
                  align: 'center',
                  render: (v: number) => (
                    <span className="font-semibold text-emerald-600">{fmt(v)} ca</span>
                  ),
                },
                {
                  title: 'Lời chúc',
                  dataIndex: 'message',
                  render: (v: string) =>
                    v || <span className="text-slate-400">—</span>,
                },
              ]}
            />
          </div>
        )}
      </div>

      {/* QR modal */}
      <Modal
        open={qrOpen}
        onCancel={() => setQrOpen(false)}
        footer={null}
        title="Mã QR chiến dịch"
        width={320}
      >
        <div className="flex flex-col items-center gap-2 py-2">
          <QRCode value={profileUrl} size={200} />
          <div className="text-sm text-slate-500 text-center">
            Quét để mở trang chiến dịch
          </div>
        </div>
      </Modal>

      {/* Donate modal */}
      <Modal
        open={donateOpen}
        onCancel={() => setDonateOpen(false)}
        onOk={submitDonate}
        okText="Ủng hộ"
        cancelText="Huỷ"
        title="Ủng hộ chiến dịch"
      >
        <div className="flex flex-col gap-3 py-1">
          {/* Mức ủng hộ nhanh */}
          <div className="flex flex-wrap gap-2">
            {DONATE_PRESETS.map((amt) => (
              <Button
                key={amt}
                shape="round"
                size="small"
                type={amount === amt ? 'primary' : 'default'}
                style={amount === amt ? { background: BRAND, borderColor: BRAND } : undefined}
                onClick={() => setAmount(amt)}
              >
                {fmt(amt)} ca
              </Button>
            ))}
          </div>
          <InputNumber
            className="w-full"
            style={{ width: '100%' }}
            min={1000}
            step={1000}
            value={amount}
            onChange={(v) => setAmount(v as number)}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            parser={(v) => Number((v || '').replace(/\./g, ''))}
            addonAfter="ca"
            placeholder="Hoặc nhập số Ca khác"
          />
          <Input
            placeholder="Lời chúc (tuỳ chọn)"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            maxLength={200}
          />
        </div>
      </Modal>

      {/* Đăng bài cập nhật */}
      <PostUpdateModal
        open={postOpen}
        onClose={() => setPostOpen(false)}
        onSubmit={async (content, imageUrl) => {
          try {
            await onPostUpdate(content, imageUrl);
            message.success('Đã đăng bài cập nhật');
          } catch (e: any) {
            message.error(e?.response?.data?.message || 'Đăng bài thất bại');
          }
        }}
      />

      {/* Yêu cầu rút tiền */}
      <WithdrawModal
        open={withdrawOpen}
        raised={c.raised}
        selfDonated={c.donors
          .filter((d) => d.userId === c.ownerId)
          .reduce((sum, d) => sum + d.amount, 0)}
        onClose={() => setWithdrawOpen(false)}
        onConfirm={async () => {
          try {
            await onWithdraw();
            message.success('Đã gửi yêu cầu quyết toán thù lao, đang giải ngân');
          } catch (e: any) {
            message.error(e?.response?.data?.message || 'Yêu cầu quyết toán thất bại');
          }
        }}
      />

      {/* Chỉnh sửa nội dung */}
      <EditContentModal
        open={editOpen}
        initial={{ title: c.title, content: c.content, imageUrl: c.imageUrl ?? undefined }}
        onClose={() => setEditOpen(false)}
        onSave={async (patch) => {
          try {
            await onEdit(patch);
            message.success('Đã cập nhật nội dung chiến dịch');
          } catch (e: any) {
            message.error(e?.response?.data?.message || 'Cập nhật thất bại');
          }
        }}
      />

      {/* eKYC camera (chụp CCCD + liveness) — mở khi chưa xác thực mà bấm rút */}
      <EkycCameraModal
        open={ekycOpen}
        onClose={() => setEkycOpen(false)}
        onVerified={async ({ bank }) => {
          try {
            await updateBankAccount(bank);
            setEkycOpen(false);
            message.success('Xác thực eKYC thành công');
            setWithdrawOpen(true);
          } catch (e: any) {
            message.error(
              e?.response?.data?.message || 'Lưu tài khoản ngân hàng thất bại'
            );
          }
        }}
      />
    </div>
  );
};

export default CampaignDetailPage;
